import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
type Module = {
  title: string;
  label: string;
  subtitle: string;
  detail: string;
  bullets: string[];
};
const modules: Module[] = [{
  label: 'Module 1',
  title: 'Dance Fitness Foundations™',
  subtitle: 'The professional framework to become a confident, effective instructor from day one.',
  detail: 'Master safety guidelines, injury prevention, body mechanics, musical interpretation, movement selection, and professional teaching habits.',
  bullets: ['Safety and injury-prevention principles', 'Movement selection for different populations', 'Professional habits that build confidence']
}, {
  label: 'Module 2',
  title: 'Ready-to-Teach Class Structures™',
  subtitle: 'Never wonder how to organize another class again.',
  detail: 'Build 30, 45, and 60-minute sessions with warm-ups, activation, cardio, choreography, recovery, cool-downs, and smooth transitions.',
  bullets: ['30, 45, and 60-minute structures', 'Balanced intensity progression', 'Engagement from beginning to end']
}, {
  label: 'Module 3',
  title: 'Choreography Library™',
  subtitle: 'Build exciting classes without repeating the same combinations.',
  detail: 'Ready-to-use dance combinations, Latin-inspired patterns, cardio sequences, footwork progressions, layering, and modifications by level.',
  bullets: ['Cardio and Latin-inspired patterns', 'Layered choreography and transitions', 'Beginner, intermediate, and advanced options']
}, {
  label: 'Module 4',
  title: 'Dance Cardio System™',
  subtitle: 'Create high-energy classes your students will love.',
  detail: 'Structure interval training, maintain class energy, adapt for fitness levels, and combine dance and cardio for fun, challenging sessions.',
  bullets: ['Cardio endurance and interval structure', 'Level-specific adaptations', 'High-energy class design']
}, {
  label: 'Module 5',
  title: 'Themed Classes™',
  subtitle: 'Keep your students excited every single week.',
  detail: 'Ready-to-use concepts for Latin parties, summer sessions, Christmas, Halloween, Valentine’s, beginner specials, events, and challenge weeks.',
  bullets: ['Seasonal and celebration classes', 'Community event ideas', 'Fresh reasons for students to invite friends']
}, {
  label: 'Module 6',
  title: 'Instructor Success™',
  subtitle: 'Become a better instructor beyond choreography.',
  detail: 'Develop professional cueing, voice projection, group leadership, motivation, mixed-level management, and retention strategies.',
  bullets: ['Clear cueing and voice projection', 'Mixed-level group management', 'Student motivation and retention']
}, {
  label: 'Module 7',
  title: 'Annual Planning System™',
  subtitle: 'Stop planning week by week. Organize your entire year at once.',
  detail: 'Use annual, monthly, and weekly frameworks, class rotations, seasonal programming, templates, and teaching calendars.',
  bullets: ['Planning frameworks and templates', 'Class rotation and seasonal programming', 'Long-term variety with less stress']
}];
export function ClassLibrary() {
  return <section className="bg-soft px-4 py-20 sm:py-24" data-id="element-39">
      <div className="mx-auto max-w-6xl" data-id="element-40">
        <div className="mb-14 text-center" data-id="element-41">
          <h2 className="font-display text-3xl font-bold text-navy sm:text-5xl" data-id="element-42">
            7 professional modules in one complete teaching system
          </h2>
          <p className="mt-3 text-base text-navy-light" data-id="element-43">
            Dance Fitness Instructor Pro™ — Complete 7-in-1 System
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2" data-id="element-44">
          {modules.map((module, index) => <motion.article key={module.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index % 2 * 0.1
        }} className={`relative flex flex-col rounded-2xl border bg-white p-7 ${index === 0 ? 'border-navy/40' : 'border-line'}`} data-id="element-45">
              {index === 0 && <span className="absolute right-5 top-5 rounded-full bg-navy px-3 py-1 text-[10px] font-bold text-white" data-id="element-46">
                  START HERE
                </span>}
              <p className="text-xs font-bold uppercase tracking-widest text-navy-light" data-id="element-47">
                {module.label}
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold text-navy" data-id="element-48">
                {module.title}
              </h3>
              <p className="mt-4 border-l-2 border-orange pl-4 text-sm font-medium italic text-navy" data-id="element-49">
                {module.subtitle}
              </p>
              <p className="mt-5 text-sm leading-relaxed text-slate-600" data-id="element-50">
                {module.detail}
              </p>
              <ul className="mt-6 space-y-2.5" data-id="element-51">
                {module.bullets.map(bullet => <li key={bullet} className="flex gap-2 text-sm text-slate-700" data-id="element-52">
                    <CheckCircle2Icon size={17} className="shrink-0 text-orange" data-id="element-53" />
                    {bullet}
                  </li>)}
              </ul>
              <p className="mt-6 rounded-xl border border-navy/15 bg-navy/5 p-3 text-sm text-navy" data-id="element-54">
                <strong data-id="element-55">Result: </strong>Teach more dynamic, structured classes
                with confidence.
              </p>
            </motion.article>)}
        </div>
      </div>
    </section>;
}